import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaPrint } from "react-icons/fa";
import API_BASE_URL from "../../../components/apiconfig/api-config";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { formatDateDDMMYY } from "../../../components/common/dateUtils";
import logoKribhcoAgri from "../../../assets/logo-kribhcoAgri.png";

const DebitNotePreview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [debitNote, setDebitNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDebitNoteDetails();
    // eslint-disable-next-line
  }, [id]);

  const fetchDebitNoteDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/sales/debit-note-preview/${id}`,
        { credentials: "include" }
      );
      const data = await response.json();

      if (response.ok) {
        setDebitNote(data.data);
        console.log("Debit Note Data:", data.data); // Debug log
      } else {
        setError(data.error || "Failed to fetch debit note details");
        toast.error(data.error || "Failed to fetch debit note details");
      }
    } catch (error) {
      console.error("Error fetching debit note details:", error);
      setError("Server Error");
      toast.error("Server Error");
    }
    setLoading(false);
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto p-4 bg-white">
        <div className="text-center text-[#0c75b2] py-10">
          Loading debit note...
        </div>
      </div>
    );
  }

  if (error || !debitNote) {
    return (
      <div className="max-w-5xl mx-auto p-4 bg-white">
        <button
          onClick={() => navigate("/debit-note")}
          className="flex items-center gap-2 text-[#0c75b2] hover:text-[#0c75b2] mb-4"
        >
          <FaArrowLeft /> Back to List
        </button>
        <div className="text-center text-red-600 py-10">
          {error || "Debit note not found"}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 bg-white">
      {/* Action Buttons - Hide on print */}
      <div className="flex justify-between items-center mb-4 print:hidden">
        <button
          onClick={() => navigate("/debit-note")}
          className="flex items-center gap-2 text-[#0c75b2] hover:text-[#0c75b2] font-medium"
        >
          <FaArrowLeft /> Back to List
        </button>
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 bg-[#0c75b2] text-white px-4 py-2 rounded hover:bg-[#0c75b2]"
        >
          <FaPrint /> Print
        </button>
      </div>

      {/* Debit Note Document */}
      <div className="border-2 border-black bg-white">
        {/* Company Header - Dynamic from Database */}
        <div className="border-b-2 border-black p-3">
          <div className="flex items-start gap-4">
            {/* Logo on Left */}
            <div className="flex-shrink-0">
              <img
                src={logoKribhcoAgri}
                alt="KRIBHCO Agri Logo"
                className="h-32 w-auto object-contain"
                style={{ maxWidth: '250px' }}
              />
            </div>
            {/* Company Details on Right */}
            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold tracking-wide">
                {debitNote.kabl_business_name || "NA"}
              </h1>
              <div className="text-xs mt-1 space-y-0.5">
                {/* Address */}
                {debitNote.kabl_address && (
                  <p>{debitNote.kabl_address}</p>
                )}
                {/* Format: STATE-PINCODE */}
                {debitNote.kabl_state && debitNote.kabl_pincode ? (
                  <p>
                    {debitNote.kabl_state.toUpperCase()}-{debitNote.kabl_pincode}
                  </p>
                ) : debitNote.kabl_state ? (
                  <p>{debitNote.kabl_state.toUpperCase()}</p>
                ) : debitNote.kabl_pincode ? (
                  <p>{debitNote.kabl_pincode}</p>
                ) : (
                  <p>NA</p>
                )}
                <p>CIN No: {debitNote.kabl_cin_no || "NA"}</p>
                <p>PAN No: {debitNote.kabl_pan || "NA"}</p>
                <p>GSTIN: {debitNote.kabl_gst_number || "NA"}</p>
                <p>State Name: {debitNote.kabl_state || "NA"}</p>
                <p>E-Mail: {debitNote.kabl_email || "NA"}</p>
                <p>www.kribhcoagri.in</p>
              </div>
            </div>
          </div>
        </div>

        {/* Document Title - Centered */}
        <div className="border-b border-black">
          <div className="text-center py-3">
            <h2 className="text-lg font-bold">DEBIT NOTE</h2>
          </div>
        </div>

        {/* Debit Note Number and BL No - Two Column Layout */}
        <div className="py-2">
          <div className="flex border-b border-black">
            {/* Left Side */}
            <div className="flex-1 border-r border-black">
              <div className="px-4 pb-2">
                {/* Party Details Section */}
                <div className="space-y-1">
                  <div>
                    <p className="text-xs font-semibold mb-0.5">Buyer Name:</p>
                    <p className="text-xs font-bold">
                      {debitNote.buyer_name || "NA"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs">
                      <span className="font-semibold">Buyer GSTIN No:</span> {debitNote.buyer_gst || "NA"}
                    </p>
                  </div>
                  {debitNote.buyer_address &&
                    (() => {
                      // Split address and format into 2-3 lines
                      const addressParts = debitNote.buyer_address
                        .split(",")
                        .map((part) => part.trim())
                        .filter((part) => part);
                      const maxLines = 3;
                      const lines = [];

                      if (addressParts.length <= maxLines) {
                        // If address has 3 or fewer parts, show each on a line
                        lines.push(...addressParts);
                      } else {
                        // If more than 3 parts, combine intelligently
                        // First line: first part
                        lines.push(addressParts[0]);
                        // Second line: combine middle parts (if any)
                        if (addressParts.length > 2) {
                          const middleParts = addressParts.slice(
                            1,
                            addressParts.length - 1
                          );
                          lines.push(middleParts.join(", "));
                        }
                        // Last line: last part (usually state/pincode)
                        if (addressParts.length > 1) {
                          lines.push(addressParts[addressParts.length - 1]);
                        }
                      }

                      return lines.map((line, idx) => (
                        <p key={idx} className="text-xs mt-0.5">
                          {line}
                        </p>
                      ));
                    })()}
                </div>
              </div>
            </div>
            
            {/* Right Side */}
            <div className="flex-1">
              <div className="px-4 pb-2">
                <div className="space-y-1.5">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-semibold">Date:</span>
                    <span className="text-xs font-bold">
                      {formatDateDDMMYY(debitNote.debit_note_date)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-semibold">DEBIT NOTE NO.-</span>
                    <span className="text-xs font-semibold">
                      {debitNote.debit_note_number || "NA"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-semibold">Ref No:</span>
                    <span className="text-xs font-semibold">
                      {debitNote.ref_no || "-"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Particulars Table */}
        <div>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-black">
                <th className="px-4 py-2 text-center font-bold">Particulars</th>
                <th className="px-4 py-2 text-center font-bold whitespace-nowrap">
                  Amount In INR
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-black">
                <td className="px-4 py-3">
                  <p className="font-medium">{debitNote.particulars || "NA"}</p>
                </td>
                <td className="px-4 py-3 text-right font-medium whitespace-nowrap">
                  ₹{" "}
                  {parseFloat(debitNote.amount || 0).toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </td>
              </tr>
              <tr className="border-b-2 border-black">
                <td className="px-4 py-2">
                  <p className="font-bold">Total In INR</p>
                </td>
                <td className="px-4 py-2 text-right font-bold whitespace-nowrap">
                  ₹{" "}
                  {parseFloat(
                    debitNote.total_amount || debitNote.amount || 0
                  ).toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Green Separator Line */}

       


        {/* Bank Details and Signature - Dynamic from Database with Issuing Authority */}
        <div className="px-4 py-3">
          <p className="text-xs mb-2 font-semibold">Company Bank Details:</p>
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <p className="text-xs mb-1">
                <span className="font-semibold">A/C Holder Name:</span>{" "}
                {debitNote.kabl_account_holder_name ||
                  debitNote.kabl_business_name ||
                  "NA"}
              </p>
              <p className="text-xs mb-1">
                <span className="font-semibold">Account Number:</span>{" "}
                {debitNote.kabl_account_no || "N/A"}
              </p>
              <p className="text-xs mb-1">
                <span className="font-semibold">Bank Name:</span>{" "}
                {debitNote.kabl_bank_name || "N/A"}
              </p>
              <p className="text-xs mb-1">
                <span className="font-semibold">Bank Address:</span>{" "}
                {debitNote.kabl_bank_address || "N/A"}
                {debitNote.kabl_bank_address_line2 && (
                  <>
                    <br />
                    <span className="ml-32">
                      {debitNote.kabl_bank_address_line2}
                    </span>
                  </>
                )}
              </p>
              <p className="text-xs mt-1">
                <span className="font-semibold">IFSC Code:</span>{" "}
                {debitNote.kabl_ifsc_code || "N/A"}
              </p>
            </div>

            {/* Signature Section - Dynamic from Database */}
            <div
              className="flex-shrink-0 ml-8 text-center"
              style={{ minWidth: "120px" }}
            >
              <div className="flex flex-col items-center justify-center h-full">
                {/* For Kribhco Agri Business Limited */}
                <p className="text-xs font-semibold mb-2">
                  For Kribhco Agri Business Limited
                </p>
                
                {/* Show Issuing Authority Details */}
                {debitNote.issuing_authority_id_check && (
                  <>
                    {/* Signature Image */}
                    {debitNote.issuing_authority_signature && (
                      <div
                        className="mb-3 flex items-center justify-center"
                        style={{ height: "60px", width: "100px" }}
                      >
                        <img
                          src={`${API_BASE_URL}/uploads/${debitNote.issuing_authority_signature}`}
                          alt={`${debitNote.issuing_authority_name} Signature`}
                          style={{
                            height: "100px",
                            width: "auto",
                            maxWidth: "150px",
                            objectFit: "contain",
                            marginTop: "20px",
                          }}
                          className="print:h-20"
                          onError={(e) => {
                            console.error("Image load error:", e);
                            e.target.style.display = "none";
                            const fallback = e.target.parentElement;
                            fallback.innerHTML =
                              '<div class="w-24 h-24 border-2 border-black rounded-full flex items-center justify-center bg-white"><div class="text-center"><p class="text-xs font-bold leading-tight">KRIBHCO</p><p class="text-xs font-bold leading-tight">AGRI BUSINESS</p><p class="text-xs font-bold leading-tight">LIMITED</p></div></div>';
                          }}
                        />
                      </div>
                    )}

                    {/* No Signature - Show Seal */}
                    {!debitNote.issuing_authority_signature && (
                      <div className="w-24 h-24 border-2 border-black rounded-full flex items-center justify-center bg-white mb-3">
                        <div className="text-center px-1">
                          <p className="text-xs font-bold leading-tight">
                            KRIBHCO AGRI
                          </p>
                          <p className="text-xs font-bold leading-tight">
                            BUSINESS
                          </p>
                          <p className="text-xs font-bold leading-tight">
                            LIMITED
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Fallback if no issuing authority */}
                {!debitNote.issuing_authority_id_check && (
                  <div className="w-24 h-24 border-2 border-black rounded-full flex items-center justify-center bg-white mb-3">
                    <div className="text-center px-1">
                      <p className="text-xs font-bold leading-tight">
                        KRIBHCO AGRI
                      </p>
                      <p className="text-xs font-bold leading-tight">
                        BUSINESS
                      </p>
                      <p className="text-xs font-bold leading-tight">LIMITED</p>
                    </div>
                  </div>
                )}
                
                {/* Authorised Signatory */}
                <p className="text-xs font-semibold mt-2">Authorised Signatory</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            margin: 0;
            padding: 0;
            background: white;
          }
          body * {
            visibility: hidden;
          }
          .max-w-4xl, .max-w-4xl * {
            visibility: visible;
          }
          .max-w-4xl {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            max-width: 100%;
            margin: 0;
            padding: 0;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:h-20 {
            height: 80px !important;
          }
          img[alt="KRIBHCO Agri Logo"] {
            max-width: 250px !important;
            height: 128px !important;
            width: auto !important;
            object-fit: contain !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .border-black {
            border-color: #000000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .border-green-500, .border-green-600 {
            border-color: #16a34a !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          [style*="border-color"] {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          table {
            border-collapse: collapse !important;
            width: 100% !important;
          }
          th, td {
            border-color: #000000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          @page {
            margin: 0.5cm;
            size: A4;
          }
        }
      `}</style>
    </div>
  );
};

export default DebitNotePreview;
